document.addEventListener('DOMContentLoaded', async () => {
  const root = document.querySelector('[id^="WishlistCustomRoot-"]');
  const grid = document.querySelector('[id^="WishlistCustomGrid-"]');
  const empty = document.querySelector('[id^="WishlistCustomEmpty-"]');
  const loading = root?.querySelector('.wishlist-custom__loading');
  const wishlistNumberWrap = document.querySelector('.js-wishlist-number span');
  const wishlistNumber = document.querySelector('.js-wishlist-number i');

  if (!root || !grid || !empty || !loading) return;

  /*
    ----------------------------------------
    Storefront API 設定
    ----------------------------------------
    ※ Admin API のアクセストークンは絶対に入れないでください。
    ※ Storefront API access token を入れてください。
  */
  const STOREFRONT_API_VERSION = '2026-07';
  const STOREFRONT_ACCESS_TOKEN = 'b37d6249be38eabd7d91de99df0102a9';
  const STOREFRONT_API_URL = `/api/${STOREFRONT_API_VERSION}/graphql.json`;

  const wishlistPath = root.dataset.wishlistPath || '/a/wishlist';
  const customerId = root.dataset.customerId || '';
  const noImageUrl = root.dataset.noImageUrl || '';

  const escapeHtml = (str) => {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  };

  const isShopifyCdnImage = (url) => {
    return typeof url === 'string' && url.includes('cdn.shopify.com');
  };

  const isNoImagePlaceholder = (url) => {
    return typeof url === 'string' && url.includes('/s/images/admin/no-image');
  };

  const buildImageUrl = (url, width) => {
    if (!isShopifyCdnImage(url) || isNoImagePlaceholder(url)) {
      return url;
    }

    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  };

  const moneyFormat = (value) => {
    const number = Number(value || 0);
    return `¥${number.toLocaleString('ja-JP')}`;
  };

  /*
    ----------------------------------------
    Smart Wishlist API
    ----------------------------------------
  */
  const getExpressWishlistIdFromCookie = () => {
    const name = 'wishlist_id=';
    const decoded = decodeURIComponent(document.cookie || '');
    const parts = decoded.split(';');

    for (let i = 0; i < parts.length; i += 1) {
      let c = parts[i];

      while (c.charAt(0) === ' ') {
        c = c.substring(1);
      }

      if (c.indexOf(name) === 0) {
        return c.substring(name.length, c.length);
      }
    }

    return '';
  };

  const getWishlistId = () => {
    if (typeof window.SWGetExpressWishlistId === 'function') {
      return window.SWGetExpressWishlistId();
    }

    return getExpressWishlistIdFromCookie();
  };

  const buildApiUrl = () => {
    if (customerId) {
      return `${wishlistPath}?type=api&customerid=${customerId}&version=1`;
    }

    const wishlistId = getWishlistId();

    if (!wishlistId) return '';

    return `${wishlistPath}?type=api&wishlistid=${wishlistId}&version=1`;
  };

  /*
    ----------------------------------------
    Storefront API helper
    ----------------------------------------
  */
  const storefrontCache = new Map();

  const fetchStorefrontProduct = async (handle) => {
    if (!handle || !STOREFRONT_ACCESS_TOKEN || STOREFRONT_ACCESS_TOKEN === 'ここにStorefront APIのアクセストークン') {
      return null;
    }

    if (storefrontCache.has(handle)) {
      return storefrontCache.get(handle);
    }

    const query = `
      query WishlistProductData($handle: String!) {
        product(handle: $handle) {
          id
          handle
          title

          options {
            id
            name
            optionValues {
              id
              name
            }
          }

          productMarks: metafield(
            namespace: "custom"
            key: "product-mark"
          ) {
            references(first: 20) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  fields {
                    key
                    value
                  }
                }
              }
            }
          }

          colorChips: metafield(
            namespace: "custom"
            key: "color_chip"
          ) {
            references(first: 20) {
              nodes {
                ... on Metaobject {
                  id
                  handle
                  fields {
                    key
                    value
                    reference {
                      ... on MediaImage {
                        image {
                          url(transform: { maxWidth: 80 })
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    try {
      const response = await fetch(STOREFRONT_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': STOREFRONT_ACCESS_TOKEN
        },
        body: JSON.stringify({
          query,
          variables: {
            handle
          }
        })
      });

      if (!response.ok) {
        throw new Error(`Storefront API error: ${response.status}`);
      }

      const json = await response.json();

      if (json.errors) {
        console.error('Storefront API errors:', json.errors);
        return null;
      }

      const product = json?.data?.product || null;

      storefrontCache.set(handle, product);

      return product;
    } catch (error) {
      console.error('Storefront product fetch error:', error);
      return null;
    }
  };

  const getMetaobjectField = (fields, key) => {
    if (!Array.isArray(fields)) return null;

    return fields.find((field) => field.key === key) || null;
  };

  const fetchColorChips = async (handle) => {
    const product = await fetchStorefrontProduct(handle);
  
    if (!product) {
      return [];
    }
  
    const options = Array.isArray(product.options)
      ? product.options
      : [];
  
    const metaobjects =
      product?.colorChips?.references?.nodes || [];
  
    const colorChips = metaobjects
      .map((metaobject) => {
        if (!metaobject) {
          return null;
        }
  
        const fields = metaobject.fields || [];
  
        const labelField = getMetaobjectField(fields, 'label');
        const hexField = getMetaobjectField(fields, 'hex');
        const swatchImageField =
          getMetaobjectField(fields, 'swatch_image');
  
        return {
          label: labelField?.value || '',
          hex: hexField?.value || '',
          swatch_image:
            swatchImageField?.reference?.image?.url || ''
        };
      })
      .filter((chip) => {
        return chip && chip.label;
      });
  
    /*
      Color Chipのlabelと一致する値を持つオプションを探す
    */
    const colorOption = options.find((option) => {
      const optionValues = Array.isArray(option.optionValues)
        ? option.optionValues
        : [];
  
      return optionValues.some((optionValue) => {
        return colorChips.some((chip) => {
          return chip.label === optionValue.name;
        });
      });
    });
  
    if (!colorOption) {
      return [];
    }
  
    /*
      実際にその商品オプションで使われている値だけ返す
    */
    const chips = colorOption.optionValues
      .map((optionValue) => {
        return colorChips.find((chip) => {
          return chip.label === optionValue.name;
        }) || null;
      })
      .filter((chip) => {
        return chip && (chip.hex || chip.swatch_image);
      });
  
    return chips.slice(0, 20);
  };

  const fetchProductMarks = async (handle) => {
    const product = await fetchStorefrontProduct(handle);
  
    if (!product) {
      return [];
    }
  
    const metaobjects =
      product?.productMarks?.references?.nodes || [];
  
    return metaobjects
      .map((metaobject) => {
        if (!metaobject) {
          return null;
        }
  
        const fields = metaobject.fields || [];
  
        const labelField =
          getMetaobjectField(fields, 'label');
  
        const hexField =
          getMetaobjectField(fields, 'hex');
  
        return {
          label: labelField?.value || '',
          hex: hexField?.value || ''
        };
      })
      .filter((mark) => {
        return mark && mark.label;
      });
  };

  /*
    ----------------------------------------
    商品ページHTMLから税区分を取得
    ----------------------------------------
    ※ colorはStorefront APIへ移行。
    ※ 税区分だけ、既存仕様に合わせて商品ページJSONから取得。
  */
  const productPageCache = new Map();

  const fetchProductPageDocument = async (handle) => {
    if (!handle) return null;

    if (productPageCache.has(handle)) {
      return productPageCache.get(handle);
    }

    try {
      const response = await fetch(`/products/${handle}`, {
        credentials: 'same-origin'
      });

      if (!response.ok) return null;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      productPageCache.set(handle, doc);

      return doc;
    } catch (error) {
      console.error('product page fetch error:', error);
      return null;
    }
  };

  const fetchProductPageInfo = async (handle, variantId) => {
    try {
      const doc = await fetchProductPageDocument(handle);
  
      if (!doc) {
        return {
          taxable: null,
          templateSuffix: ''
        };
      }
  
      const jsonScript = doc.querySelector('[id^="ProductTaxInfo-"]');
  
      if (!jsonScript) {
        return {
          taxable: null,
          templateSuffix: ''
        };
      }
  
      const data = JSON.parse(jsonScript.textContent);
  
      return {
        taxable:
          data?.variants?.[String(variantId)]?.taxable ?? null,
        templateSuffix:
          data?.template_suffix || ''
      };
    } catch (error) {
      console.error('product page info fetch error:', error);
  
      return {
        taxable: null,
        templateSuffix: ''
      };
    }
  };

  /*
    ----------------------------------------
    HTML生成
    ----------------------------------------
  */
  const renderProductImage = ({ imageUrl, title }) => {
    const safeTitle = escapeHtml(title || '');

    if (imageUrl && !isNoImagePlaceholder(imageUrl)) {
      const src = buildImageUrl(imageUrl, 456);

      return `
        <p class="c-item-cls__img c-img">
          <img
            src="${escapeHtml(src)}"
            width="228"
            height="228"
            alt="${safeTitle}"
            loading="lazy"
          >
        </p>
      `;
    }

    return `
      <p class="c-item-cls__img c-img">
        <img
          src="${escapeHtml(noImageUrl)}"
          width="228"
          height="228"
          alt="${safeTitle}"
          loading="lazy"
        >
      </p>
    `;
  };

  const renderColorChips = (chips) => {
    if (!chips || !chips.length) {
      return '';
    }

    const chipsHtml = chips.map((chip) => {
      const hex = chip.hex || '';
      const swatchImage = chip.swatch_image || '';

      if (swatchImage) {
        return `<li style="background-image: url('${escapeHtml(swatchImage)}');"></li>`;
      }

      if (hex) {
        return `<li style="background-color: ${escapeHtml(hex)};"></li>`;
      }

      return '';
    }).join('');

    if (!chipsHtml) {
      return '';
    }

    return `<ul class="color">${chipsHtml}</ul>`;
  };

  const renderProductMarks = (productMarks) => {
    if (!Array.isArray(productMarks) || !productMarks.length) {
      return '';
    }
  
    const marksHtml = productMarks
      .map((mark) => {
        const label = mark.label || '';
        const hex = mark.hex || '';
  
        if (!label) {
          return '';
        }
  
        const style = hex
          ? ` style="--bgcolor-cat: ${escapeHtml(hex)};"`
          : '';
  
        return `
          <span class="cat"${style}>
            ${escapeHtml(label)}
          </span>
        `;
      })
      .join('');
  
    if (!marksHtml) {
      return '';
    }
  
    return `
      <div class="lst-cat">
        ${marksHtml}
      </div>
    `;
  };

  const renderItem = async (item) => {
    const productId = item.id || '';
    const variantId = item.variant_id || '';
    const productUrl = `/products/${item.handle}${variantId ? `?variant=${variantId}` : ''}`;
    const imageUrl = item.image || '';
    const titleRaw = item.title || '';
    const title = escapeHtml(titleRaw);
    const variantTitle = escapeHtml(item.variant_title || '');
  
    const productPageInfo = await fetchProductPageInfo(
      item.handle,
      variantId
    );
  
    const taxable = productPageInfo.taxable;
    const templateSuffix = productPageInfo.templateSuffix;
  
    const taxLabel =
      taxable === null
        ? ''
        : taxable
          ? '（税込）'
          : '（非課税）';
  
    const displayPrice =
      templateSuffix === 'ordermade'
        ? `${moneyFormat(item.variant_price)}~`
        : moneyFormat(item.variant_price);
  
    const priceHtml =
      templateSuffix === 'buhin'
        ? '<p class="txt_note">※価格はお問い合わせください</p>'
        : `
          <p class="price">
            <span>${displayPrice}</span>
            ${taxLabel}
          </p>
        `;
  
    const chips = await fetchColorChips(item.handle);
    const productMarks = await fetchProductMarks(item.handle);
  
    return `
      <div class="c-item-cls wishlist-custom__item">
        <!-- <span
          class="smartwishlist"
          data-product="${escapeHtml(productId)}"
          data-variant="${escapeHtml(variantId)}"
        ></span> -->
  
        <a href="${escapeHtml(productUrl)}" class="link">
          ${renderProductImage({
            imageUrl,
            title: titleRaw
          })}

          ${renderProductMarks(productMarks)}
  
          <div class="c-item-cls__ct">
            <h3 class="ttl">${title}</h3>
  
            ${priceHtml}
  
            ${renderColorChips(chips)}
          </div>
        </a>
  
        <div class="wishlist-custom__actions">
          <button
            type="button"
            class="btn-delete button button--secondary wishlist-custom__remove"
            data-product-id="${escapeHtml(productId)}"
            data-variant-id="${escapeHtml(variantId)}"
          >
            削除
          </button>
        </div>
      </div>
    `;
  };

  const showEmpty = () => {
    if (wishlistNumber) {
      wishlistNumber.textContent = '0';
    }
  
    if (wishlistNumberWrap) {
      wishlistNumberWrap.classList.add('is-loaded');
    }
  
    loading.hidden = true;
    grid.hidden = true;
    empty.hidden = false;
  };

  /*
    ----------------------------------------
    初期表示
    ----------------------------------------
  */
  const apiUrl = buildApiUrl();

  if (!apiUrl) {
    showEmpty();
    return;
  }

  try {
    const response = await fetch(apiUrl, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(`Wishlist API error: ${response.status}`);
    }

    const data = await response.json();

    if (wishlistNumber) {
      wishlistNumber.textContent = Number(data.count || 0).toLocaleString('ja-JP');
    }

    if (wishlistNumberWrap) {
      wishlistNumberWrap.classList.add('is-loaded');
    }

    if (!data || !Number(data.count) || !Array.isArray(data.items) || data.items.length === 0) {
      showEmpty();
      return;
    }

    const itemsHtml = await Promise.all(data.items.map(renderItem));

    grid.innerHTML = itemsHtml.join('');

    if (typeof window.SmartWishlistMain === 'function') {
      window.SmartWishlistMain();
    } else if (typeof SmartWishlistMain === 'function') {
      SmartWishlistMain();
    }

    loading.hidden = true;
    empty.hidden = true;
    grid.hidden = false;
  } catch (error) {
    console.error('Wishlist custom load error:', error);
    loading.textContent = 'お気に入り商品の読み込みに失敗しました。';
    return;
  }

  /*
    ----------------------------------------
    削除・カート追加
    ----------------------------------------
  */
  grid.addEventListener('click', async (event) => {
    const removeButton = event.target.closest('.wishlist-custom__remove');
    const addCartButton = event.target.closest('.wishlist-custom__add-cart');

    if (removeButton) {
      const productId = removeButton.dataset.productId;
      const variantId = removeButton.dataset.variantId;

      if (!productId || !variantId) {
        console.error('Wishlist remove failed: productId or variantId is missing.', {
          productId,
          variantId
        });
        return;
      }

      if (typeof window.RemoveFromSmartWishlist !== 'function') {
        console.error('RemoveFromSmartWishlist is not available.');
        return;
      }

      try {
        window.RemoveFromSmartWishlist(productId, variantId);

        removeButton.closest('.wishlist-custom__item')?.remove();

        if (wishlistNumber) {
          const currentCount = Number(wishlistNumber.textContent.replace(/,/g, '') || 0);
          wishlistNumber.textContent = Math.max(currentCount - 1, 0).toLocaleString('ja-JP');
        }

        if (!grid.querySelector('.wishlist-custom__item')) {
          showEmpty();
        }
      } catch (error) {
        console.error('Wishlist remove failed:', error);
      }

      return;
    }

    if (addCartButton) {
      const variantId = addCartButton.dataset.variantId;

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            id: Number(variantId),
            quantity: 1
          })
        });

        if (!res.ok) {
          throw new Error('Cart add failed');
        }

        addCartButton.textContent = '追加しました';
      } catch (error) {
        console.error(error);
        addCartButton.textContent = '追加失敗';
      }
    }
  });
});